import type { CommunicationInputData } from '../../../../../hooks/Marketing';
import type { DefinitionFormData } from '../CampaignDefinitionStep';
import type { TargetingFormData } from '../CampaignTargetingStep';
import { OfferFormData, emptyOffer, offerInputToForm } from '../offers/offerForm';
import { CHANNEL_BY_ID, CHANNEL_ID } from '../channels';
import { withDefaultTime } from '../dateUtils';

type Payload = Record<string, unknown> | null;

const readBoolean = (payload: Payload, key: string, fallback: boolean): boolean => {
  const value = payload?.[key];
  return typeof value === 'boolean' ? value : fallback;
};

const readString = (payload: Payload, key: string): string => {
  const value = payload?.[key];
  return typeof value === 'string' ? value : '';
};

const readNumber = (payload: Payload, key: string): number | null => {
  const value = payload?.[key];
  return typeof value === 'number' ? value : null;
};

const readListId = (payload: Payload): string | number | null => {
  const value = payload?._listId;
  return typeof value === 'string' || typeof value === 'number' ? value : null;
};

const readNumbers = (payload: Payload, key: string): number[] => {
  const value = payload?.[key];
  return Array.isArray(value) ? value.filter((id): id is number => typeof id === 'number') : [];
};

const INITIAL_DEFINITION: DefinitionFormData = {
  channel: '', title: '', description: '', sendDate: '', sendByCustomer: false,
  hasOffer: true, followUpDateStart: '', followUpDateEnd: '', isTestOperation: false,
  isRecurring: false, isPreEdited: false, noCreation: false, editionDateStart: '', editionDateEnd: '',
};

const INITIAL_TARGETING: TargetingFormData = {
  title: '', description: '', queryId: null, queryDescription: '', nbRecipients: null, listId: null, cashdeskTarget: null,
};

export const initialOffers = (): OfferFormData[] => [emptyOffer('1')];

export const definitionFromComm = (comm: CommunicationInputData | null): DefinitionFormData => {
  if (!comm) return INITIAL_DEFINITION;
  const payload = comm.creationPayload;
  return {
    channel: CHANNEL_BY_ID[comm.defChannelId] ?? '',
    title: comm.defTitle,
    description: comm.defDescription ?? '',
    sendDate: withDefaultTime(comm.defSendStart),
    sendByCustomer: comm.defChannelId === CHANNEL_ID.cashdesk,
    hasOffer: readBoolean(payload, '_hasOffer', true),
    followUpDateStart: readString(payload, '_followUpDateStart'),
    followUpDateEnd: readString(payload, '_followUpDateEnd'),
    isTestOperation: readBoolean(payload, '_isTestOperation', false),
    isRecurring: readBoolean(payload, '_isRecurring', false),
    isPreEdited: readBoolean(payload, '_isPreEdited', false),
    noCreation: readBoolean(payload, '_noCreation', false),
    editionDateStart: withDefaultTime(payload?._editionDateStart),
    editionDateEnd: withDefaultTime(payload?._editionDateEnd, true),
  };
};

export const targetingFromComm = (comm: CommunicationInputData | null): TargetingFormData => {
  if (!comm) return INITIAL_TARGETING;
  const payload = comm.creationPayload;
  return {
    title: comm.targetTitle ?? '',
    description: comm.targetDescription ?? '',
    queryId: comm.targetQueryId ?? null,
    queryDescription: readString(payload, '_queryDesc'),
    nbRecipients: null,
    listId: readListId(payload),
    cashdeskTarget: comm.targetCategoryId
      ? {
          targetCategoryId: comm.targetCategoryId,
          identifierTypeId: readNumber(payload, '_cashdeskIdentifierTypeId'),
          entityIds: readNumbers(payload, '_cashdeskEntityIds'),
        }
      : null,
  };
};

export const offersFromComm = (comm: CommunicationInputData | null): OfferFormData[] =>
  comm?.offerInputs?.length ? comm.offerInputs.map(offerInputToForm) : initialOffers();
